import { roleToRouteMap } from 'app/components/AuthRouter/authRoutes';
import { rolesEnum } from 'app/components/AuthRouter/data/type';
import { invalidateCsrfCache, fetchCsrfToken, getApiBaseUrl } from 'utils/csrf';
import {
  clearServerProfile,
  getServerProfile,
} from 'utils/serverProfileCache';

/** Decode JWT payload (client-side, no signature verification) for UX-only fields trusted from issuer. */
function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(
          c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2),
        )
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Merge stored login blob with **JWT payload** for roles/scopes/dealer flags so
 * client-side edits to localStorage cannot escalate privileges (pentest P-CR-1).
 * Non-security UX fields (e.g. sidebar_navigation_mappings, old_layout) stay from stored API response.
 */
/** Normalize JWT `roles` field (string or array) for `.includes()` checks. */
export function normalizeRoles(roles: unknown): string[] {
  if (roles == null) return [];
  if (Array.isArray(roles)) return roles.map(String);
  return [String(roles)];
}

export function getTrustedUserProfile(shouldRedirect: boolean = true) {
  const fromServer = getServerProfile();
  if (fromServer != null && fromServer.user_id != null) {
    return fromServer as any;
  }

  /** Legacy Bearer: JWT in localStorage — roles/scopes still merged from token payload. */
  const storedRaw = localStorage.getItem('user_token');
  if (!storedRaw) {
    if (shouldRedirect) resetAndLogin();
    return null as any;
  }
  let stored: Record<string, any>;
  try {
    stored = JSON.parse(storedRaw);
  } catch {
    if (shouldRedirect) resetAndLogin();
    return null as any;
  }
  if (!Object.keys(stored).length && shouldRedirect) {
    resetAndLogin();
    return null as any;
  }
  const token = stored.accesstoken as string | undefined;
  if (token) {
    const payload = decodeJwtPayload(token);
    const data = (payload?.data || {}) as Record<string, any>;
    return {
      ...stored,
      roles: data.roles !== undefined ? data.roles : stored.roles,
      scopes: data.scopes !== undefined ? data.scopes : stored.scopes,
      access_to_all_dealership:
        data.access_to_all_dealership !== undefined
          ? data.access_to_all_dealership
          : stored.access_to_all_dealership,
      email: data.email !== undefined ? data.email : stored.email,
      user_id: data._id !== undefined ? data._id : stored.user_id,
      accesstoken: token,
    };
  }
  /** Old persisted cookie-session blobs without JWT are not trusted for roles/scopes (P-CR-1). */
  if (shouldRedirect) resetAndLogin();
  return null as any;
}

/**
 * Gets the auth header from localStorage.
 *
 * @return {object} Returns an object with header if logged in.
 */
/**
 * Bearer token for Authorization header. Empty when using HttpOnly cookie session
 * (API authenticates via cookie).
 */
export function header(shouldRedirect: boolean = true) {
  try {
    const user = JSON.parse(localStorage.getItem('user_token') || '{}');
    if (user?.accesstoken) {
      return user.accesstoken as string;
    }
  } catch {
    /* ignore */
  }
  return '';
}

export function getUUIDToken() {
  const user = getTrustedUserProfile(false);
  if (user?.user_id == null) {
    localStorage.clear();
    clearServerProfile();
    clearCacheData();
    clearCookieData();
    window.location.href = '/login';
    return '';
  }
  return `cb7e6bff-20b7-47c1-b731-fc494c9a0a1a`;
}

/** True when persisted profile exists (cookie session or legacy Bearer blob). Does not redirect. */
export function isAuthenticated(): boolean {
  const u = getTrustedUserProfile(false);
  return !!(u && u.user_id != null);
}

export const hasRoles = () => {
  const user = getTrustedUserProfile(false);

  if (!user || !Object.keys(user).length) {
    return resetAndLogin();
  }

  const userRoles = user.roles || '';
  // const isSuperUser = userRoles.find(item => item === rolesEnum.superUser);

  if (userRoles === rolesEnum.salesUser || userRoles === rolesEnum.salesPerson)
    return false;

  return true;
};

/**
 * Gets the user profile from localStorage.
 *
 * @return {object} Returns an object with user profile if logged in.
 */
export function userProfile(shouldRedirect: boolean = true) {
  return getTrustedUserProfile(shouldRedirect);
}
function hasScope(requiredScope: string) {
  const user = getTrustedUserProfile(false);
  if (!user) return false;
  const userScopes = user.scopes || [];
  if (!Array.isArray(userScopes)) return false;

  return userScopes.includes(requiredScope);
}
export const isAllowedToViewActivityMetrics = () => {
  return hasScope('view:activity_metrics');
};

export const validateEmail = inputText => {
  if (!inputText) return false;

  if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(inputText)) {
    return true;
  }

  return false;
};

export const clearCacheData = () => {
  try {
    // Clear service worker caches
    caches.keys().then(names => {
      names.forEach(name => {
        caches.delete(name);
      });
    });
    
    // Clear sessionStorage items that might cause stale data
    sessionStorage.removeItem('cache_cleared');
    
    // Force fresh fetch for all subsequent requests
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(function(registrations) {
        for(let registration of registrations) {
          registration.update();
        }
      });
    }
  } catch (error) {
    console.log('no cache');
  }
};

export const clearCookieData = () => {
  document.cookie.split(';').forEach(c => {
    document.cookie = c
      .replace(/^ +/, '')
      .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
  });
};

export const checkRole = (userRoles, pageRoles) => {
  try {
    let hasAccess = false;
    userRoles?.map(role => {
      if (pageRoles.includes(role)) {
        hasAccess = true;
      }
    });

    return hasAccess;
  } catch (error) {
    console.log(error);
  }
};

// Function to get default navigation link from sidebar_navigation_mappings (isDefault: true)
// This is specifically for NewAIAvatarLayout users (beta_maiq_performance_manager and dealership_executive)
export const getDefaultNavigationLink = (): string | null => {
  try {
    const user = userProfile();
    if (!user) {
      return null;
    }

    // Get sidebar_navigation_mappings from trusted profile (API / in-memory cache)
    const mappings = user?.sidebar_navigation_mappings || [];
    
    if (!Array.isArray(mappings) || mappings.length === 0) {
      return null;
    }

    // Recursive function to find item with isDefault: true
    const findDefaultItem = (items: any[]): any => {
      for (const item of items) {
        // Check if current item has isDefault: true
        if (item.isDefault === true && item.link) {
          return item;
        }
        
        // If item has nested items, search recursively
        if (item.items && Array.isArray(item.items) && item.items.length > 0) {
          const found = findDefaultItem(item.items);
          if (found) {
            return found;
          }
        }
      }
      return null;
    };

    const defaultItem = findDefaultItem(mappings);
    
    if (defaultItem && defaultItem.link) {
      return defaultItem.link;
    }

    return null;
  } catch (error) {
    console.error('Error getting default navigation link:', error);
    return null;
  }
};

export const getRoleBasedHomePage = () => {
  try {
    const user = userProfile();
    const prioritizedRoles = [
      rolesEnum.betaPerformanceManager,
      rolesEnum.superAdmin,
      rolesEnum.superUser,
      rolesEnum.dealershipGroupExecutive,
      rolesEnum.dealershipExecutive,
      rolesEnum.techUser,
      rolesEnum.salesUser,
      rolesEnum.salesPerson,
      rolesEnum.performanceManager,
      'user',
    ]; // Define role priorities
    const roleList = normalizeRoles(user?.roles);
    let homepage = null;

    for (const role of prioritizedRoles) {
      if (roleList.includes(role)) {
        homepage = roleToRouteMap[role];
        break;
      }
    }
    // Return default homepage if no role matches
    return homepage || '/dashboard';
  } catch (error) {
    console.log(error);
    return '/dashboard'; // Return default homepage in case of error
  }
};

export const getRoutes = (): any[] => {
  try {
    const p = getServerProfile();
    const routes = p?.resource_mappings;
    return Array.isArray(routes) ? routes : [];
  } catch (error) {
    console.error('getRoutes error:', error);
    return [];
  }
};

export const getGroupDashboardURL = (selectedDealer: any = null): string => {
  try {
    return selectedDealer?.business_group_dashboard_url ?? '';
  } catch (error) {
    console.log(error);
    return '';
  }
};

const resetAndLogin = () => {
  if (isLoginRedirectInProgress) return;
  isLoginRedirectInProgress = true;

  try {
    clearServerProfile();
    localStorage.clear();
    clearCacheData();
    clearCookieData();
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  } catch (error) {
    console.log(error);
    isLoginRedirectInProgress = false;
  }
};

let isLoginRedirectInProgress = false;
let isLogoutInProgress = false;

/** True while logout teardown + login redirect is in progress (avoids UI flash e.g. 404). */
export const isLoggingOut = (): boolean => isLogoutInProgress;

export const handleLogout = async () => {
  if (isLogoutInProgress) return;
  isLogoutInProgress = true;

  const removeStorageItemSafely = (key: string) => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Ignore storage access failures (private mode/quota/security restrictions).
    }
  };

  try {
    const base = getApiBaseUrl();
    const { default: userApiService } = await import(
      'app/apiService/user/userApiService'
    );
    await userApiService.postLogOutEvent().catch(() => {});
    invalidateCsrfCache();
    const csrf = await fetchCsrfToken().catch(() => '');
    await fetch(`${base}/api/v2/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
      },
      body: '{}',
    }).catch(() => {});
  } catch (error) {
    console.log('logout error', error);
  } finally {
    invalidateCsrfCache();
    removeStorageItemSafely('user_token');
    removeStorageItemSafely('app-theme');
    clearCacheData();
    // Kick off navigation before clearing the in-memory profile so layout
    // listeners do not re-render an empty route tree (404 flash) first.
    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
    clearServerProfile();
  }
};
